import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL || '';

const buildSignatureImageUrl = (fileName) => {
  if (!fileName) return null;
  if (fileName.startsWith('http') || fileName.startsWith('blob:')) return fileName;
  const base = IMAGE_URL.replace(/\/$/, '');
  const path = String(fileName).replace(/^\//, '');
  return `${base}/${path}`;
};

const isSignatureUploadSuccess = (response) => {
  const data = response?.data;
  if (!data || data.isSuccessful === false) return false;
  const code = data.statusCode;
  return (code === 200 || code === 201) && Boolean(String(data.result ?? '').trim());
};

const SignatureUploadField = ({ value, onChange, disabled = false }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);

  const imageUrl = localPreviewUrl || buildSignatureImageUrl(value);

  useEffect(() => {
    if (!value) {
      setLocalPreviewUrl(null);
    }
  }, [value]);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.warning('Please select a single image file (PNG, JPG, GIF, or WebP).');
      event.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const apiService = new ApiService();
      const response = await apiService.uploadSignatureImage(file);

      if (isSignatureUploadSuccess(response)) {
        const fileName = String(response.data.result).trim();
        onChange(fileName);
        setLocalPreviewUrl(URL.createObjectURL(file));
        toast.success(response?.data?.message || 'Signature uploaded successfully');
      } else {
        toast.error(response?.data?.message || 'Failed to upload signature');
      }
    } catch (error) {
      console.error('Signature upload error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to upload signature');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemove = () => {
    if (localPreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    onChange('');
    setLocalPreviewUrl(null);
    setPreviewOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="w-70 d-flex flex-column gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          className="d-none"
          disabled={disabled || uploading}
          onChange={handleFileSelect}
        />
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <button type="button" className="mainbtn" disabled={disabled || uploading} onClick={() => fileInputRef.current?.click()}>
            {uploading ? <ClipLoader color="#ffffff" size={18} /> : 'Upload Signature'}
          </button>
          {value && (
            <>
              <button type="button" className="mainbtnRed" disabled={uploading} onClick={() => setPreviewOpen(true)}>
                View
              </button>
              {!disabled && (
                <button type="button" className="mainbtnGry" disabled={uploading} onClick={handleRemove}>
                  Remove
                </button>
              )}
            </>
          )}
        </div>
        {value && (
          <small className="text-muted text-truncate" title={value}>
            {value}
          </small>
        )}
      </div>

      <Modal show={previewOpen} onHide={() => setPreviewOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Signature Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Signature"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              onError={() => toast.error('Could not load signature image')}
            />
          ) : (
            <p className="text-muted mb-0">No preview available.</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SignatureUploadField;
